import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, MinusCircle, ArrowRight, Save, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "./Modal";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";

const STEP_ROUTE = {
  country: "/app/create",
  guests: "/app/create",
  deco: "/app/decoration",
  ceia: "/app/ceia",
  gifts: "/app/gifts",
};

export function PlanProgressEditor({ open, progress, onClose, onSaved, onDeleted, startDelete = false }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [delStage, setDelStage] = useState(null); // null | 'confirm' | 'done' | 'error'
  const [deleting, setDeleting] = useState(false);
  const [ssWarn, setSsWarn] = useState([]);
  const [naSet, setNaSet] = useState(() => new Set(progress?.not_applicable || []));

  useEffect(() => {
    if (open) setNaSet(new Set(progress?.not_applicable || []));
  }, [open, progress]);
  // Opened straight from the hero "Excluir planejamento" button: skip the editor and go to the confirmation.
  useEffect(() => {
    if (!open || !startDelete) return;
    setSsWarn([]);
    setDelStage("confirm");
    api.post("/analytics/event", { event: "christmas_plan_delete_started", meta: {} }).catch(() => {});
    api.get("/christmas/delete-preview").then(({ data }) => {
      if (data?.secret_santa?.has_activity) setSsWarn(data.secret_santa.groups || []);
    }).catch(() => {});
  }, [open, startDelete]);

  if (!open) return null;

  const steps = progress?.steps || ["country", "guests", "deco", "ceia", "gifts"];
  const liveStates = progress?.live?.states || {};
  const savedStates = progress?.saved?.states || {};
  const rawDone = (k) => liveStates[k] === "completed";
  const isNa = (k) => naSet.has(k);
  const currentState = (k) => (isNa(k) ? "not_applicable" : rawDone(k) ? "completed" : "incomplete");
  const applicableKeys = steps.filter((k) => !isNa(k));
  const completed = applicableKeys.filter(rawDone).length;
  const applicable = applicableKeys.length;
  const provisionalPct = applicable ? Math.round((completed / applicable) * 100) : 0;

  const dirty = steps.some((k) => currentState(k) !== (savedStates[k] || "incomplete"));

  const toggleNa = (k) => {
    setNaSet((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  };

  const attemptClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const goToStep = (k) => navigate(STEP_ROUTE[k] || "/app");

  const doSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/progress/save", { not_applicable: [...naSet] });
      toast.success(t("plan.saved_toast"));
      onSaved?.(data);
    } catch {
      toast.error("—");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    api.post("/analytics/event", { event: "christmas_plan_changes_discarded", meta: {} }).catch(() => {});
    setConfirmClose(false);
    onClose();
  };

  const saveAndExit = async () => {
    await doSave();
    setConfirmClose(false);
    onClose();
  };

  const openDelete = async () => {
    api.post("/analytics/event", { event: "christmas_plan_delete_started", meta: {} }).catch(() => {});
    setSsWarn([]);
    setDelStage("confirm");
    try {
      const { data } = await api.get("/christmas/delete-preview");
      if (data?.secret_santa?.has_activity) setSsWarn(data.secret_santa.groups || []);
    } catch { /* preview is best-effort */ }
  };

  const cancelDelete = () => {
    api.post("/analytics/event", { event: "christmas_plan_delete_cancelled", meta: {} }).catch(() => {});
    setDelStage(null);
    if (startDelete) onClose();
  };

  const confirmDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete("/christmas");
      setDelStage("done");
    } catch {
      setDelStage("error");
    } finally {
      setDeleting(false);
    }
  };

  const goCreate = () => {
    setDelStage(null);
    onDeleted?.();
    onClose();
    navigate("/app/create");
  };

  const editorOpen = open && !confirmClose && !delStage;

  return (
    <>
      <Modal open={editorOpen} onClose={attemptClose} title={t("plan.editor_title")} testid="plan-editor-modal">
        <p className="-mt-2 mb-4 text-sm text-xmas-slate">{t("plan.editor_sub")}</p>

        <div className="mb-5 rounded-2xl border border-xmas-gold/20 bg-xmas-navy/40 p-4" data-testid="plan-editor-progress">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-xmas-gold">{t("plan.provisional")}</span>
            <span className="font-mono-num text-2xl text-xmas-gold" data-testid="plan-editor-percent">{provisionalPct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-xmas-navysurface">
            <div className="h-full rounded-full bg-gradient-to-r from-xmas-pine to-xmas-gold transition-all duration-500" style={{ width: `${provisionalPct}%` }} />
          </div>
          <p className="mt-2 text-xs text-xmas-slate">{t("plan.steps_done", { done: completed, total: applicable })}</p>
        </div>

        <div className="space-y-2">
          {steps.map((k) => {
            const na = isNa(k);
            const done = rawDone(k);
            return (
              <div
                key={k}
                data-testid={`plan-editor-step-${k}`}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 transition ${
                  na ? "border-white/5 bg-white/[0.01] opacity-70" : done ? "border-xmas-gold/30 bg-xmas-gold/5" : "border-white/10 bg-white/[0.02]"
                }`}
              >
                <button type="button" onClick={() => goToStep(k)} data-testid={`plan-editor-open-${k}`} className="flex flex-1 items-center gap-3 text-left">
                  {na ? <MinusCircle className="h-5 w-5 shrink-0 text-xmas-slate" /> : done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-xmas-gold" /> : <Circle className="h-5 w-5 shrink-0 text-xmas-slate" />}
                  <span className={`flex-1 text-sm ${na ? "text-xmas-slate line-through" : done ? "text-xmas-snow" : "text-xmas-champagne"}`}>{t(`task.${k}`)}</span>
                  {na ? (
                    <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-xmas-slate">{t("plan.na_tag")}</span>
                  ) : (
                    <span className={`text-[11px] uppercase tracking-wide ${done ? "text-xmas-gold" : "text-xmas-slate"}`}>{done ? t("plan.done") : t("plan.open")}</span>
                  )}
                </button>
                <button type="button" onClick={() => toggleNa(k)} data-testid={`plan-editor-na-${k}`} className="shrink-0 rounded-full px-2.5 py-1 text-[11px] text-xmas-slate transition hover:text-xmas-champagne">
                  {na ? t("plan.na_applicable") : t("plan.na_mark")}
                </button>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={doSave}
          disabled={saving}
          data-testid="plan-editor-save"
          className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-wider disabled:opacity-60"
        >
          <Save className="h-4 w-4" /> {saving ? t("plan.saving") : t("plan.save")}
        </button>

        {/* Secondary, discreet destructive action */}
        <button
          type="button"
          onClick={openDelete}
          data-testid="plan-editor-delete"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full px-6 py-2.5 text-xs font-medium text-xmas-slate transition hover:text-xmas-crimson"
        >
          <Trash2 className="h-3.5 w-3.5" /> {t("plan.delete")}
        </button>
      </Modal>

      {/* Unsaved-changes guard */}
      <Modal open={confirmClose} onClose={() => setConfirmClose(false)} title={t("plan.unsaved_title")} testid="plan-unsaved-modal" centerTitle>
        <p className="mb-6 text-center text-sm text-xmas-champagne">{t("plan.unsaved_body")}</p>
        <div className="space-y-2.5">
          <button type="button" onClick={() => setConfirmClose(false)} data-testid="unsaved-keep" className="w-full rounded-full border border-xmas-gold/40 px-5 py-3 text-sm font-semibold text-xmas-snow hover:bg-white/5">
            {t("plan.keep_editing")}
          </button>
          <button type="button" onClick={saveAndExit} disabled={saving} data-testid="unsaved-save-exit" className="btn-gold w-full rounded-full px-5 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
            {saving ? t("plan.saving") : t("plan.save_and_exit")}
          </button>
          <button type="button" onClick={discard} data-testid="unsaved-discard" className="w-full rounded-full px-5 py-3 text-sm text-xmas-slate hover:text-xmas-snow">
            {t("plan.discard")}
          </button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={delStage === "confirm"} onClose={cancelDelete} title={t("plan.del_title")} testid="plan-delete-modal" centerTitle>
        <p className="mb-4 text-sm text-xmas-champagne">{t("plan.del_body")}</p>
        <ul className="mb-4 space-y-1.5 rounded-2xl border border-xmas-crimson/25 bg-xmas-crimson/5 p-4 text-sm text-xmas-champagne">
          <li className="flex gap-2"><span className="text-xmas-crimson">•</span>{t("plan.del_b1")}</li>
          <li className="flex gap-2"><span className="text-xmas-crimson">•</span>{t("plan.del_b2")}</li>
          <li className="flex gap-2"><span className="text-xmas-crimson">•</span>{t("plan.del_b3")}</li>
          <li className="flex gap-2"><span className="text-xmas-gold">•</span>{t("plan.del_b5")}</li>
          <li className="flex gap-2"><span className="text-xmas-gold">•</span>{t("plan.del_b4")}</li>
        </ul>
        {ssWarn.length > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border border-xmas-gold/25 bg-xmas-gold/5 p-3 text-xs text-xmas-champagne" data-testid="plan-delete-ss-note">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-xmas-gold" />
            <span>{t("plan.del_ss_note")}</span>
          </div>
        )}
        <p className="mb-1 text-center text-sm font-semibold text-xmas-snow">{t("plan.del_sure")}</p>
        <p className="mb-5 text-center text-[11px] text-xmas-slate">{t("plan.del_note_account")}</p>
        <div className="space-y-2.5">
          <button type="button" onClick={cancelDelete} data-testid="plan-delete-cancel" className="w-full rounded-full border border-xmas-gold/40 px-5 py-3 text-sm font-semibold text-xmas-snow hover:bg-white/5">
            {t("plan.del_cancel")}
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleting}
            data-testid="plan-delete-confirm"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-xmas-crimson px-5 py-3 text-sm font-bold uppercase tracking-wider text-white transition hover:brightness-110 disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" /> {deleting ? t("plan.del_deleting") : t("plan.del_confirm")}
          </button>
        </div>
      </Modal>

      {/* Delete success */}
      <Modal open={delStage === "done"} onClose={goCreate} title={t("plan.del_ok_title")} testid="plan-delete-done-modal" centerTitle>
        <p className="mb-6 text-center text-sm text-xmas-champagne">{t("plan.del_ok_body")}</p>
        <button type="button" onClick={goCreate} data-testid="plan-delete-create" className="btn-gold w-full rounded-full px-5 py-3.5 text-sm font-bold uppercase tracking-wider">
          ✨ {t("plan.del_ok_cta")}
        </button>
      </Modal>

      {/* Delete error */}
      <Modal open={delStage === "error"} onClose={() => setDelStage(null)} title={t("plan.del_err_title")} testid="plan-delete-error-modal" centerTitle>
        <p className="mb-6 text-center text-sm text-xmas-champagne">{t("plan.del_err_body")}</p>
        <div className="space-y-2.5">
          <button type="button" onClick={confirmDelete} disabled={deleting} data-testid="plan-delete-retry" className="btn-gold w-full rounded-full px-5 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
            {deleting ? t("plan.del_deleting") : t("plan.del_retry")}
          </button>
          <button type="button" onClick={() => setDelStage(null)} className="w-full rounded-full px-5 py-3 text-sm text-xmas-slate hover:text-xmas-snow">
            {t("plan.del_cancel")}
          </button>
        </div>
      </Modal>
    </>
  );
}
